// src/utils/layoutManager.js
export const SINGLE_WINDOW_LAYOUTS = [
  { name: 'center', label: 'Center' },
  { name: 'fill', label: 'Fill Screen' },
  { name: 'leftHalf', label: 'Left 50%' },
  { name: 'rightHalf', label: 'Right 50%' },
  { name: 'topHalf', label: 'Top 50%' },
  { name: 'bottomHalf', label: 'Bottom 50%' },
  { name: 'upperLeft', label: 'Upper Left' },
  { name: 'upperRight', label: 'Upper Right' },
  { name: 'lowerLeft', label: 'Lower Left' },
  { name: 'lowerRight', label: 'Lower Right' },
  { name: 'fillVertical', label: 'Fill Vertical' },
  { name: 'fillHorizontal', label: 'Fill Horizontal' },
];

export const MULTI_WINDOW_LAYOUTS = [
  { name: '2_side_by_side', label: '2 Side by Side' },
  { name: '3_in_a_row', label: '3 in a Row' },
  { name: '4_quadrants', label: '4 Quadrants' },
  { name: 'pyramid', label: 'Pyramid' },
];

function getSingleWindowLayoutPositions(layoutName, viewportWidth, viewportHeight, translateX, translateY, scale) {
  const worldW = viewportWidth / scale;
  const worldH = viewportHeight / scale;
  const worldX = translateX;
  const worldY = translateY;

  let x = worldX, y = worldY, w = 800, h = 600;
  const margin = 20;

  switch (layoutName) {
    case 'center':
      w = worldW * 0.8;
      h = worldH * 0.8;
      x = worldX + (worldW - w)/2;
      y = worldY + (worldH - h)/2;
      break;
    case 'fill':
      w = worldW;
      h = worldH;
      x = worldX;
      y = worldY;
      break;
    case 'leftHalf':
      w = worldW/2;
      h = worldH;
      x = worldX;
      y = worldY;
      break;
    case 'rightHalf':
      w = worldW/2;
      h = worldH;
      x = worldX + worldW/2;
      y = worldY;
      break;
    case 'topHalf':
      w = worldW;
      h = worldH/2;
      x = worldX;
      y = worldY;
      break;
    case 'bottomHalf':
      w = worldW;
      h = worldH/2;
      x = worldX;
      y = worldY + worldH/2;
      break;
    case 'upperLeft':
      w = worldW/2 - margin;
      h = worldH/2 - margin;
      x = worldX;
      y = worldY;
      break;
    case 'upperRight':
      w = worldW/2 - margin;
      h = worldH/2 - margin;
      x = worldX + worldW/2 + margin;
      y = worldY;
      break;
    case 'lowerLeft':
      w = worldW/2 - margin;
      h = worldH/2 - margin;
      x = worldX;
      y = worldY + worldH/2 + margin;
      break;
    case 'lowerRight':
      w = worldW/2 - margin;
      h = worldH/2 - margin;
      x = worldX + worldW/2 + margin;
      y = worldY + worldH/2 + margin;
      break;
    case 'fillVertical':
      // Fill entire vertical, half width, centered horizontally
      h = worldH;
      w = worldW * 0.5;
      x = worldX + (worldW - w)/2;
      y = worldY;
      break;
    case 'fillHorizontal':
      // Fill entire horizontal, half height, centered vertically
      w = worldW;
      h = worldH * 0.5;
      x = worldX;
      y = worldY + (worldH - h)/2;
      break;
    default:
      w = worldW * 0.8;
      h = worldH * 0.8;
      x = worldX + (worldW - w)/2;
      y = worldY + (worldH - h)/2;
      break;
  }

  return [{ x, y, width: w, height: h }];
}

function getMultiWindowLayoutPositions(layoutName, count, viewportWidth, viewportHeight, translateX, translateY, scale) {
  const worldW = viewportWidth / scale;
  const worldH = viewportHeight / scale;
  const worldX = translateX;
  const worldY = translateY;
  const positions = [];

  switch (layoutName) {
    case 'pyramid':
      // Pyramid specifically for 3 apps:
      // One all the way across the top (full width top half)
      // Two on the bottom row, each half width, bottom half
      if (count === 3) {
        const topW = worldW;
        const topH = worldH/2;
        // Top app
        positions.push({ x: worldX, y: worldY, width: topW, height: topH });
        // Bottom row: 2 apps side by side
        const bottomH = worldH/2;
        const halfW = worldW/2;
        positions.push({ x: worldX, y: worldY+topH, width: halfW, height: bottomH });
        positions.push({ x: worldX+halfW, y: worldY+topH, width: halfW, height: bottomH });
      } else {
        // If not exactly 3, fallback to row layout
        for (let i = 0; i < count; i++) {
          const w = worldW / count;
          const h = worldH;
          const x = worldX + i * w;
          const y = worldY;
          positions.push({ x, y, width: w, height: h });
        }
      }
      break;
    case '2_side_by_side':
      for (let i = 0; i < count; i++) {
        const w = worldW / count;
        const h = worldH;
        const x = worldX + i * w;
        const y = worldY;
        positions.push({ x, y, width: w, height: h });
      }
      break;
    case '3_in_a_row':
      for (let i = 0; i < count; i++) {
        const w = worldW / count;
        const h = worldH;
        const x = worldX + i * w;
        const y = worldY;
        positions.push({ x, y, width: w, height: h });
      }
      break;
    case '4_quadrants':
      {
        const perRow = Math.ceil(Math.sqrt(count));
        const w = worldW / perRow;
        const h = worldH / perRow;
        let idx = 0;
        for (let row = 0; row < perRow; row++) {
          for (let col = 0; col < perRow; col++) {
            if (idx < count) {
              const x = worldX + col * w;
              const y = worldY + row * h;
              positions.push({ x, y, width: w, height: h });
              idx++;
            }
          }
        }
      }
      break;
    default:
      // fallback: single row
      for (let i = 0; i < count; i++) {
        const w = worldW / count;
        const h = worldH;
        const x = worldX + i * w;
        const y = worldY;
        positions.push({ x, y, width: w, height: h });
      }
      break;
  }

  return positions;
}

export function getLayoutPositions(layoutName, appCount, viewportWidth, viewportHeight, translateX, translateY, scale) {
  if (appCount === 1) {
    return getSingleWindowLayoutPositions(layoutName, viewportWidth, viewportHeight, translateX, translateY, scale);
  } else {
    return getMultiWindowLayoutPositions(layoutName, appCount, viewportWidth, viewportHeight, translateX, translateY, scale);
  }
}
